import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import { getSites, getUtilityReport } from "../api";
import VestaSelect from "../components/common/VestaSelect";
import VestaDateInput from "../components/common/VestaDateInput";
import { createDocxTableBlob, createXlsxBlob } from "../utils/officeExport";
import "../assets/css/windows.css";
import "../assets/css/bookings.css";
import "../assets/css/management.css";
import "../assets/css/utility.css";

const PRINT_ITEMS = [
  { title: "Arrivi del Giorno", description: "Mostra l'elenco completo degli ospiti in arrivo nel giorno selezionato, con camera assegnata, orario previsto, recapiti, numero ospiti e note operative utili per preparare un check-in ordinato e senza dimenticanze.", fields: ["Data arrivo", "Camera", "Cliente", "Numero ospiti", "Note check-in"] },
  { title: "Partenze del Giorno", description: "Riepiloga tutte le camere in partenza nel giorno selezionato, indicando saldo, orario previsto di uscita, stato operativo della camera e attività necessarie prima della successiva disponibilità.", fields: ["Data partenza", "Camera", "Cliente", "Saldo", "Stato camera"] },
  { title: "Planning Camere", description: "Presenta una vista ordinata delle occupazioni delle camere nel periodo selezionato, utile per controllare prenotazioni, stati, trattamenti previsti e continuità operativa tra arrivi e partenze.", fields: ["Periodo", "Camera", "Prenotazione", "Stato", "Trattamento"] },
  { title: "Lista Ospiti Presenti", description: "Elenca i clienti attualmente presenti in struttura, con dati essenziali per reception, sicurezza interna, controllo giornaliero delle presenze e verifica rapida delle date di soggiorno.", fields: ["Camera", "Cliente", "Documento", "Check-in", "Check-out"] },
  { title: "Schedina Cliente", description: "Raccoglie la scheda anagrafica del cliente con documento, dati fiscali, recapiti, consensi privacy e storico sintetico dei soggiorni, così da avere un profilo completo e consultabile.", fields: ["Anagrafica", "Documento", "Recapiti", "Consensi", "Storico"] },
  { title: "Registro Pulizie", description: "Organizza le camere da pulire, rifare o controllare, mostrando priorità, addetto incaricato e note operative per il reparto housekeeping, con attenzione alle urgenze di giornata.", fields: ["Camera", "Stato", "Priorità", "Addetto", "Note"] },
  { title: "Ricevute e Fatture", description: "Riepiloga i documenti fiscali emessi nel periodo selezionato, includendo numero, data, cliente, imponibile e totale documento, per controllare rapidamente contabilità e quadrature.", fields: ["Numero", "Data", "Cliente", "Imponibile", "Totale"] },
  { title: "Incassi Giornalieri", description: "Mostra il totale degli incassi registrati nella giornata, suddivisi per metodo di pagamento, operatore di cassa e note di riferimento, così da verificare il movimento economico giornaliero.", fields: ["Data", "Metodo", "Operatore", "Importo", "Note"] },
  { title: "Tassa di Soggiorno", description: "Calcola il prospetto della tassa di soggiorno per periodo, distinguendo ospiti tassabili, esenzioni, notti registrate e importi dovuti, pronto per controllo amministrativo.", fields: ["Periodo", "Cliente", "Notti", "Esenzioni", "Totale tassa"] },
  { title: "Elenco Manutenzioni", description: "Elenca gli interventi di manutenzione aperti, in corso o chiusi su camere e aree comuni, con priorità, stato avanzamento e data di risoluzione prevista o completata.", fields: ["Area", "Problema", "Priorità", "Stato", "Data chiusura"] },
  { title: "Prenotazioni per Periodo", description: "Elenco completo delle prenotazioni che interessano il periodo selezionato, con camera, cliente, stato e valore del soggiorno.", fields: ["Periodo", "Camera", "Cliente", "Stato", "Valore"] },
  { title: "Soggiorni Conclusi", description: "Riepiloga i soggiorni conclusi nel periodo e il relativo documento fiscale, utile per controllare chiusure e fatturazione.", fields: ["Data chiusura", "Camera", "Cliente", "Documento", "Totale"] },
  { title: "Prenotazioni Cancellate", description: "Elenca le prenotazioni cancellate nel periodo con cliente, camera, valore previsto e note disponibili.", fields: ["Data arrivo", "Camera", "Cliente", "Valore", "Note"] },
  { title: "Soggiorni In House", description: "Elenco operativo dei soggiorni attualmente In House con date, camera, cliente e saldo stimato.", fields: ["Check-in", "Check-out", "Camera", "Cliente", "Saldo"] },
  { title: "Servizi Addebitati", description: "Dettaglia tutti i servizi addebitati nel periodo con quantità, imponibile, IVA e totale ivato.", fields: ["Data", "Servizio", "Quantità", "IVA", "Totale"] },
  { title: "Pagamenti e Acconti", description: "Riepiloga pagamenti, acconti e caparre registrati sulle prenotazioni nel periodo selezionato.", fields: ["Prenotazione", "Cliente", "Tipo", "Metodo", "Importo"] },
  { title: "Documenti Annullati e Stornati", description: "Mostra fatture, ricevute, note di credito e storni annullati o emessi come rettifica, mantenendo lo storico fiscale consultabile.", fields: ["Numero", "Data", "Cliente", "Tipo", "Stato"] },
  { title: "Castelletto IVA", description: "Raggruppa imponibile, imposta e totale per aliquota IVA dei documenti fiscali emessi nel periodo.", fields: ["Aliquota IVA", "Imponibile", "Imposta", "Totale", "Documenti"] },
  { title: "Fatturato per Cliente", description: "Riepiloga il fatturato fiscale per cliente, con numero documenti, imponibile, imposta e totale.", fields: ["Cliente", "Documenti", "Imponibile", "Imposta", "Totale"] },
  { title: "Documenti per Tipologia", description: "Riepiloga i documenti fiscali per tipologia e stato, utile per quadrature amministrative e controlli di registro.", fields: ["Tipo", "Stato", "Documenti", "Imponibile", "Totale"] },
];

const STAT_ITEMS = [
  { title: "Occupazione Camere", description: "Analizza la percentuale di occupazione delle camere per periodo, sede e tipologia, evidenziando disponibilità, andamento operativo e capacità della struttura di trasformare le camere disponibili in presenze reali.", fields: ["Periodo", "Sede", "Tipologia", "Camere disponibili", "Occupazione %"] },
  { title: "Permanenza Media", description: "Calcola il numero medio di notti per prenotazione nel periodo selezionato e confronta l'andamento con i periodi precedenti, così da leggere durata dei soggiorni e tendenza commerciale.", fields: ["Periodo", "Notti totali", "Prenotazioni", "Media notti", "Trend"] },
  { title: "Canali di Prenotazione", description: "Mostra la distribuzione delle prenotazioni per canale di provenienza, confrontando volumi, notti generate, valore economico e peso percentuale, per capire quali canali lavorano meglio.", fields: ["Canale", "Prenotazioni", "Notti", "Valore", "Peso %"] },
  { title: "Nazionalità Clienti", description: "Analizza la provenienza degli ospiti per nazionalità, indicando clienti, presenze, pernottamenti e peso sul totale della struttura, utile per leggere mercati, abitudini e stagionalità.", fields: ["Nazionalità", "Clienti", "Presenze", "Notti", "Peso %"] },
  { title: "Cancellazioni", description: "Riepiloga le prenotazioni cancellate nel periodo, con motivo, camera coinvolta, valore economico perso e canale di origine, per individuare criticità ricorrenti e possibili azioni correttive.", fields: ["Data", "Motivo", "Camera", "Valore perso", "Canale"] },
  { title: "No Show", description: "Elenca le prenotazioni senza presentazione del cliente, indicando canale, importo previsto e stato dell'eventuale incasso o penale, così da controllare perdite e recuperi economici.", fields: ["Data arrivo", "Cliente", "Canale", "Importo", "Stato incasso"] },
  { title: "Check-in per Fascia Oraria", description: "Distribuisce gli arrivi per fascia oraria, così da organizzare meglio il presidio della reception nei momenti di maggiore afflusso e preparare personale, documenti e camere con anticipo.", fields: ["Fascia", "Arrivi", "Sede", "Giorno settimana", "Picco"] },
  { title: "Utilizzo Servizi Extra", description: "Analizza quantità e valore dei servizi extra venduti nel periodo, collegando ricavi, camere e clienti quando disponibili, per valutare quali servizi generano più interesse e fatturato.", fields: ["Servizio", "Quantità", "Ricavo", "Camera", "Cliente"] },
  { title: "Performance Sedi", description: "Confronta le sedi della struttura su occupazione, prenotazioni, presenze e variazione rispetto al periodo precedente, offrendo una lettura immediata delle performance operative.", fields: ["Sede", "Occupazione", "Prenotazioni", "Presenze", "Variazione"] },
  { title: "Storico Clienti Ricorrenti", description: "Evidenzia i clienti con più soggiorni, mostrando frequenza di ritorno, notti generate, ultimo arrivo e valore economico complessivo, utile per fidelizzazione e azioni mirate.", fields: ["Cliente", "Soggiorni", "Notti", "Ultimo arrivo", "Valore totale"] },
  { title: "Occupazione per Tipologia Camera", description: "Confronta l'occupazione delle diverse tipologie camera per individuare categorie più richieste e periodi di saturazione.", fields: ["Tipologia", "Prenotazioni", "Notti", "Camere", "Occupazione %"] },
  { title: "Lead Time Prenotazioni", description: "Analizza con quanti giorni di anticipo vengono inserite le prenotazioni rispetto al check-in.", fields: ["Fascia anticipo", "Prenotazioni", "Notti", "Valore", "Peso %"] },
  { title: "Trend Mensile Prenotazioni", description: "Raggruppa prenotazioni, notti e valore per mese per evidenziare stagionalità e variazioni operative.", fields: ["Mese", "Prenotazioni", "Notti", "Valore", "Occupazione %"] },
  { title: "Ricavi per Documento", description: "Analizza quantità e valore dei documenti fiscali emessi per tipologia nel periodo selezionato.", fields: ["Tipo documento", "Documenti", "Imponibile", "Imposta", "Totale"] },
  { title: "IVA per Aliquota", description: "Analizza la composizione fiscale delle vendite per aliquota IVA, con imponibile, imposta e incidenza percentuale.", fields: ["Aliquota IVA", "Righe", "Imponibile", "Imposta", "Incidenza %"] },
  { title: "Servizi per Tipologia", description: "Classifica i servizi addebitati per descrizione mostrando quantità, imponibile, IVA e ricavo lordo.", fields: ["Servizio", "Quantità", "Imponibile", "IVA", "Totale"] },
  { title: "Clienti Singoli e Ricorrenti", description: "Confronta i clienti con un solo soggiorno e quelli con più soggiorni nel periodo selezionato per misurare la fidelizzazione.", fields: ["Categoria", "Clienti", "Soggiorni", "Notti", "Peso %"] },
  { title: "Distribuzione Durata Soggiorni", description: "Distribuisce le prenotazioni per durata del soggiorno, utile per comprendere il mix tra brevi e lunghe permanenze.", fields: ["Durata", "Prenotazioni", "Notti", "Valore", "Peso %"] },
  { title: "Andamento Documenti Fiscali", description: "Mostra l'andamento temporale dei documenti fiscali emessi con imponibile, imposta e totale per giorno.", fields: ["Data", "Documenti", "Imponibile", "Imposta", "Totale"] },
  { title: "Incidenza Cancellazioni", description: "Misura il rapporto tra prenotazioni cancellate e prenotazioni complessive nel periodo.", fields: ["Periodo", "Prenotazioni", "Cancellate", "Incidenza %", "Valore perso"] },
];

const PROFIT_ITEMS = [
  { title: "Ricavo per Camera Disponibile", description: "Calcola il ricavo medio generato da ogni camera disponibile, confrontando periodo, sede, ricavi camere e andamento rispetto allo storico, per misurare il rendimento reale del parco camere.", fields: ["Periodo", "Camere disponibili", "Ricavi camere", "RevPAR", "Trend"] },
  { title: "Prezzo Medio Camera", description: "Mostra il prezzo medio di vendita delle camere nel periodo selezionato, escludendo extra e tasse, così da leggere il rendimento puro delle camere e confrontarlo con le strategie tariffarie.", fields: ["Periodo", "Camere vendute", "Ricavi camere", "ADR", "Variazione"] },
  { title: "Margine per Prenotazione", description: "Stima il margine netto di ogni prenotazione sottraendo dal ricavo lordo commissioni, costi operativi e altri costi collegati, per valutare la convenienza economica reale.", fields: ["Prenotazione", "Ricavo", "Commissioni", "Costi", "Margine"] },
  { title: "Redditività per Canale", description: "Confronta la redditività dei canali di vendita in base a ricavi, commissioni sostenute, margine netto e incidenza sul totale, distinguendo volume generato e reale convenienza.", fields: ["Canale", "Ricavi", "Commissioni", "Margine", "Peso %"] },
  { title: "Redditività per Tipologia Camera", description: "Analizza la redditività di ogni categoria camera, mostrando notti vendute, ricavo prodotto, prezzo medio e margine stimato, per capire quali tipologie rendono davvero.", fields: ["Tipologia", "Notti vendute", "Ricavo", "ADR", "Margine"] },
  { title: "Extra più Redditizi", description: "Classifica i servizi extra più redditizi, confrontando quantità venduta, ricavo generato, costo stimato e margine economico, così da valorizzare i servizi più performanti.", fields: ["Servizio", "Quantità", "Ricavo", "Costo stimato", "Margine"] },
  { title: "Incasso per Sede", description: "Riepiloga gli incassi per sede separando camere, servizi extra, tasse e totale complessivo del periodo selezionato, per leggere rapidamente il contributo economico di ogni struttura.", fields: ["Sede", "Camere", "Extra", "Tasse", "Totale"] },
  { title: "Scostamento Budget", description: "Confronta gli obiettivi previsionali con i ricavi consuntivi del periodo, evidenziando scostamento economico e percentuale, così da capire dove il risultato supera o manca il budget.", fields: ["Periodo", "Budget", "Consuntivo", "Scostamento", "Scostamento %"] },
  { title: "Giorni più Redditizi", description: "Individua i giorni con maggiore ricavo e margine, collegando il risultato a occupazione, eventi e condizioni operative, per riconoscere i momenti più redditizi del calendario.", fields: ["Data", "Occupazione", "Ricavo", "Margine", "Evento"] },
  { title: "Previsione Ricavi", description: "Stima i ricavi futuri partendo dalle prenotazioni confermate, dallo storico della struttura e dal livello di rischio del periodo, offrendo una previsione leggibile per decisioni commerciali e operative.", fields: ["Periodo futuro", "Prenotato", "Storico", "Stima", "Rischio"] },
  { title: "Ticket Medio Soggiorno", description: "Calcola il valore medio per soggiorno concluso separando imponibile, IVA e totale lordo.", fields: ["Periodo", "Soggiorni", "Imponibile", "Totale", "Ticket medio"] },
  { title: "Incidenza Servizi Extra", description: "Misura quanto i servizi extra incidono sul valore complessivo dei soggiorni e individua opportunità di upselling.", fields: ["Periodo", "Ricavi soggiorni", "Ricavi extra", "Totale", "Incidenza extra %"] },
  { title: "Valore Cliente", description: "Classifica i clienti per valore fiscale generato, numero di documenti e valore medio documento.", fields: ["Cliente", "Documenti", "Totale", "Media documento", "Peso %"] },
  { title: "Mix Documenti Fiscali", description: "Confronta fatture, ricevute, note di credito e storni per quantità e valore economico.", fields: ["Tipo", "Documenti", "Imponibile", "Imposta", "Totale"] },
  { title: "Margine Servizi Extra", description: "Analizza il contributo dei servizi extra usando ricavo netto e una stima prudenziale del costo operativo.", fields: ["Servizio", "Quantità", "Ricavo netto", "Costo stimato", "Margine stimato"] },
  { title: "Ricavo per Notte Venduta", description: "Calcola il ricavo fiscale medio per notte effettivamente venduta nel periodo.", fields: ["Periodo", "Notti vendute", "Ricavo", "Ricavo/notte", "Documenti"] },
  { title: "Concentrazione Fatturato Clienti", description: "Evidenzia quanto il fatturato dipende dai clienti principali e il loro peso sul totale.", fields: ["Cliente", "Totale", "Peso %", "Documenti", "Classifica"] },
  { title: "Imponibile IVA e Lordo", description: "Riepilogo economico-fiscale del periodo con imponibile, IVA, totale lordo e aliquota media effettiva.", fields: ["Periodo", "Imponibile", "IVA", "Lordo", "IVA media %"] },
];

const ROWS_PER_PAGE = 28;

const DATE_FIELD_PATTERN = /data|periodo|giorno|check-in|check-out|ultimo arrivo|ultimo pernottamento/i;
const COMPANY_FALLBACK = "VestaMNG";

export function getCompanyDisplayName() {
  try {
    const tenant = JSON.parse(localStorage.getItem("tenant") || "null");
    const user = JSON.parse(localStorage.getItem("user") || "null");
    return tenant?.companyDisplayName || tenant?.displayName || user?.companyName || COMPANY_FALLBACK;
  } catch {
    return COMPANY_FALLBACK;
  }
}

function isDateLikeField(field) {
  return DATE_FIELD_PATTERN.test(String(field || ""));
}

function getColumnFilterFields(activeItem) {
  return activeItem?.fields || [];
}

function formatDateForSummary(value, fallback) {
  return value || fallback;
}

function parseDateValue(value) {
  const text = String(value || "").trim();
  const ddmmyyyy = text.match(/(\d{2})\/(\d{2})\/(\d{4})/);
  if (ddmmyyyy) return new Date(`${ddmmyyyy[3]}-${ddmmyyyy[2]}-${ddmmyyyy[1]}T00:00:00`);
  const yyyymmdd = text.match(/(\d{4})-(\d{2})-(\d{2})/);
  if (yyyymmdd) return new Date(`${yyyymmdd[1]}-${yyyymmdd[2]}-${yyyymmdd[3]}T00:00:00`);
  const monthNames = { gennaio: 0, febbraio: 1, marzo: 2, aprile: 3, maggio: 4, giugno: 5, luglio: 6, agosto: 7, settembre: 8, ottobre: 9, novembre: 10, dicembre: 11 };
  const monthMatch = text.toLowerCase().match(/(gennaio|febbraio|marzo|aprile|maggio|giugno|luglio|agosto|settembre|ottobre|novembre|dicembre)\s+(\d{4})/);
  if (monthMatch) return new Date(Number(monthMatch[2]), monthNames[monthMatch[1]], 1);
  return null;
}

function rowMatchesDateRange(activeItem, row, filters) {
  if (!filters.from && !filters.to) return true;
  const dateFieldIndex = (activeItem.fields || []).findIndex(isDateLikeField);
  if (dateFieldIndex < 0) return true;
  const rowDate = parseDateValue(row[dateFieldIndex]);
  if (!rowDate || Number.isNaN(rowDate.getTime())) return true;
  const fromDate = filters.from ? new Date(`${filters.from}T00:00:00`) : null;
  const toDate = filters.to ? new Date(`${filters.to}T23:59:59`) : null;
  if (fromDate && rowDate < fromDate) return false;
  if (toDate && rowDate > toDate) return false;
  return true;
}

export const EXPORT_FORMATS = [
  { value: "pdf", label: "PDF" },
  { value: "csv", label: "CSV" },
  { value: "excel", label: "Excel" },
  { value: "word", label: "Word" },
];

export function UtilityOrientationIcon({ orientation = "portrait" }) {
  return orientation === "landscape" ? (
    <svg className="utility_orientation_icon is_landscape" viewBox="0 0 24 24" aria-hidden="true"><rect x="3.5" y="7" width="17" height="10" rx="1.7" /></svg>
  ) : (
    <svg className="utility_orientation_icon is_portrait" viewBox="0 0 24 24" aria-hidden="true"><rect x="7" y="3.5" width="10" height="17" rx="1.7" /></svg>
  );
}

export function UtilityActionIconButton({ action, onClick, orientation = "portrait", title = "", ariaLabel = "", className = "", disabled = false }) {
  const activeOrientationLabel = orientation === "landscape" ? "Foglio orizzontale" : "Foglio verticale";
  const labels = {
    print: "Stampa",
    export: "Esporta / Download",
    preview: "Espandi tutto",
    orientation: activeOrientationLabel,
  };
  const label = title || labels[action] || "Azione";
  const buttonClass = [
    "utility_icon_action_button",
    action === "orientation" ? "utility_orientation_button" : "",
    className,
  ].filter(Boolean).join(" ");

  let icon = null;
  if (action === "orientation") icon = <UtilityOrientationIcon orientation={orientation} />;
  if (action === "preview") icon = <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M8 3H3v5M16 3h5v5M8 21H3v-5M16 21h5v-5" /></svg>;
  if (action === "print") icon = <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 8V3h10v5M7 17H5a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2M7 14h10v7H7z" /></svg>;
  if (action === "export") icon = <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 3v12m0 0 5-5m-5 5-5-5M5 19h14" /></svg>;

  return (
    <button type="button" className={buttonClass} onClick={onClick} aria-label={ariaLabel || label} title={label} disabled={disabled}>
      {icon}
    </button>
  );
}

export function printUtilityPreview(orientation = "portrait") {
  const styleId = "vesta-print-orientation-style";
  document.getElementById(styleId)?.remove();
  const style = document.createElement("style");
  style.id = styleId;
  style.textContent = `@media print { @page { size: ${orientation === "landscape" ? "A4 landscape" : "A4"}; margin: 0; } }`;
  document.head.appendChild(style);
  document.body.classList.toggle("utility_print_landscape", orientation === "landscape");
  document.body.classList.toggle("utility_print_portrait", orientation !== "landscape");
  window.setTimeout(() => window.print(), 0);
}

export function UtilityFullscreenPreview({ open, orientation = "portrait", currentPage = 0, pageCount = 1, loading = false, onPageChange, onPrint, onExport, onToggleOrientation, onClose, children }) {
  if (!open) return null;
  const lastPage = Math.max(Number(pageCount || 1) - 1, 0);
  const safePage = Math.min(Math.max(Number(currentPage || 0), 0), lastPage);
  const goToPage = (page) => onPageChange?.(Math.min(Math.max(page, 0), lastPage));
  return createPortal(
    <div className="utility_fullscreen_preview" role="dialog" aria-modal="true" aria-label="Anteprima documento a tutto schermo">
      <div className="utility_fullscreen_toolbar">
        <div className="utility_fullscreen_toolbar_spacer" aria-hidden="true" />
        <div className="utility_page_controls" aria-label="Navigazione pagine anteprima a tutto schermo">
          <button type="button" onClick={() => goToPage(0)} disabled={safePage === 0} aria-label="Prima pagina">«</button>
          <button type="button" onClick={() => goToPage(safePage - 1)} disabled={safePage === 0} aria-label="Pagina precedente">‹</button>
          <strong>{safePage + 1} / {lastPage + 1}</strong>
          <button type="button" onClick={() => goToPage(safePage + 1)} disabled={safePage >= lastPage} aria-label="Pagina successiva">›</button>
          <button type="button" onClick={() => goToPage(lastPage)} disabled={safePage >= lastPage} aria-label="Ultima pagina">»</button>
        </div>
        <div className="utility_fullscreen_toolbar_right">
          <div className="utility_fullscreen_actions">
            <UtilityActionIconButton action="orientation" orientation={orientation} onClick={onToggleOrientation} />
            <UtilityActionIconButton action="print" onClick={onPrint} />
            <UtilityActionIconButton action="export" onClick={onExport} />
          </div>
          <button type="button" className="utility_icon_action_button utility_fullscreen_exit" onClick={onClose} aria-label="Esci da tutto schermo" title="Esci da tutto schermo">×</button>
        </div>
      </div>
      <div className={`utility_fullscreen_body${loading ? " is_loading" : ""}`}>{children}{loading && <div className="utility_loading_overlay" role="status" aria-live="polite"><span className="utility_loading_spinner" aria-hidden="true"><span /></span><strong>Caricamento dati</strong></div>}</div>
    </div>,
    window.document.body
  );
}

export function UtilityExportOverlay({ open, title, formats = null, documentMode = false, error = "", onClose, onExport }) {
  if (!open) return null;
  const visibleFormats = Array.isArray(formats) && formats.length
    ? formats.map((value) => EXPORT_FORMATS.find((format) => format.value === value)).filter(Boolean)
    : EXPORT_FORMATS;
  return (
    <div className={`overlay_backdrop utility_export_overlay${documentMode ? " utility_export_document_overlay" : ""}`} role="dialog" aria-modal="true" aria-label="Scegli formato esportazione">
      <div className={`overlay_card utility_export_card${documentMode ? " utility_export_document_card" : ""}`}>
        <div className="overlay_header utility_export_head">
          {documentMode ? (
            <h2>ESPORTA DOCUMENTO</h2>
          ) : (
            <div>
              <span>Esporta file</span>
              <strong>{String(title || "DOCUMENTO").toUpperCase()}</strong>
            </div>
          )}
          <button type="button" className="overlay_close_button utility_export_close" onClick={onClose} aria-label="Chiudi esportazione">×</button>
        </div>
        <div className="overlay_body utility_export_body">
          {!documentMode && <p>Scegli il formato. Il file viene generato con intestazione, filtri e tabella ordinata.</p>}
          <div className="utility_export_grid">
            {visibleFormats.map((format) => (
              <button key={format.value} type="button" className="overlay_save_button utility_export_format_button" onClick={() => onExport?.(format.value)}>{format.label}</button>
            ))}
          </div>
          {error ? <div className="overlay_error_message">{error}</div> : null}
        </div>
      </div>
    </div>
  );
}

function csvEscape(value) {
  return `"${String(value ?? "").replace(/"/g, '""')}"`;
}

function htmlEscape(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

export function slugify(value) {
  return String(value || "report")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/gi, "-")
    .replace(/^-+|-+$/g, "") || "report";
}

function cleanPdfText(value) {
  return String(value ?? "")
    .replace(/[\u2018\u2019]/g, "'")
    .replace(/[\u201C\u201D]/g, '"')
    .replace(/[\u2013\u2014]/g, "-")
    .replace(/€/g, "EUR")
    .replace(/[^\x09\x0A\x0D\x20-\x7E\xA0-\xFF]/g, "");
}

function pdfEscape(value) {
  return cleanPdfText(value).replace(/\\/g, "\\\\").replace(/\(/g, "\\(").replace(/\)/g, "\\)");
}

function wrapPdfText(value, maxChars) {
  const text = cleanPdfText(value).trim();
  if (!text) return [""];
  const words = text.split(/\s+/);
  const lines = [];
  let current = "";
  words.forEach((word) => {
    const next = current ? `${current} ${word}` : word;
    if (next.length <= maxChars) {
      current = next;
      return;
    }
    if (current) lines.push(current);
    if (word.length > maxChars) {
      for (let index = 0; index < word.length; index += maxChars) {
        const part = word.slice(index, index + maxChars);
        if (part.length === maxChars) lines.push(part);
        else current = part;
      }
      return;
    }
    current = word;
  });
  if (current) lines.push(current);
  return lines.length ? lines : [""];
}

function createPdfBlob(activeItem, pages, filters, companyName, orientation = "portrait") {
  const landscape = orientation === "landscape";
  const width = landscape ? 841.89 : 595.28;
  const height = landscape ? 595.28 : 841.89;
  const margin = 40;
  const usableWidth = width - margin * 2;
  const fields = activeItem.fields || [];
  const colWidth = usableWidth / Math.max(fields.length, 1);
  const filterLines = wrapPdfText(buildFilterSummary(filters, activeItem).join(" · ").toUpperCase(), 116);
  const objects = [];

  const addObject = (content) => {
    objects.push(content);
    return objects.length;
  };

  const fontRegular = addObject("<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>");
  const fontBold = addObject("<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold >>");
  const pageObjectIds = [];

  pages.forEach((pageRows, pageIndex) => {
    const commands = ["q", "1 1 1 rg", `0 0 ${width.toFixed(2)} ${height.toFixed(2)} re f`, "0 0 0 rg"];
    const text = (x, y, size, value, font = "F1") => {
      commands.push(`BT /${font} ${size} Tf ${x.toFixed(2)} ${y.toFixed(2)} Td (${pdfEscape(value)}) Tj ET`);
    };
    const line = (x1, y1, x2, y2, shade = "0.84") => {
      commands.push(`${shade} G 0.6 w ${x1.toFixed(2)} ${y1.toFixed(2)} m ${x2.toFixed(2)} ${y2.toFixed(2)} l S 0 G`);
    };
    const rect = (x, y, w, h, fill) => {
      if (fill) commands.push(`${fill} rg ${x.toFixed(2)} ${y.toFixed(2)} ${w.toFixed(2)} ${h.toFixed(2)} re f 0 0 0 rg`);
      commands.push(`0.78 G 0.5 w ${x.toFixed(2)} ${y.toFixed(2)} ${w.toFixed(2)} ${h.toFixed(2)} re S 0 G`);
    };

    let y = height - margin;
    text(margin, y, 12, String(companyName || COMPANY_FALLBACK).toUpperCase(), "F2");
    text(width - margin - 78, height - margin, 9, `Pagina ${pageIndex + 1} di ${pages.length}`, "F2");
    y -= 10;
    line(margin, y, width - margin, y);
    y -= 15;
    text(margin, y, 11, String(activeItem.title || "").toUpperCase(), "F2");
    y -= 9;
    line(margin, y, width - margin, y);
    y -= 15;
    filterLines.slice(0, 3).forEach((filterLine) => {
      text(margin, y, 7.5, filterLine, "F2");
      y -= 10;
    });
    y -= 4;

    const headerHeight = 22;
    fields.forEach((field, fieldIndex) => {
      const x = margin + fieldIndex * colWidth;
      rect(x, y - headerHeight, colWidth, headerHeight, "0.93 0.97 0.99");
      wrapPdfText(field.toUpperCase(), Math.max(8, Math.floor(colWidth / 5.4))).slice(0, 2).forEach((lineText, lineIndex) => {
        text(x + 4, y - 9 - lineIndex * 8, 7, lineText, "F2");
      });
    });
    y -= headerHeight;

    pageRows.forEach((row) => {
      const wrappedCells = fields.map((_, cellIndex) => wrapPdfText(row[cellIndex] || "", Math.max(8, Math.floor(colWidth / 5.2))).slice(0, 3));
      const rowHeight = Math.max(20, Math.max(...wrappedCells.map((lines) => lines.length)) * 8 + 8);
      wrappedCells.forEach((lines, cellIndex) => {
        const x = margin + cellIndex * colWidth;
        rect(x, y - rowHeight, colWidth, rowHeight);
        lines.forEach((lineText, lineIndex) => {
          text(x + 4, y - 10 - lineIndex * 8, 7, lineText);
        });
      });
      y -= rowHeight;
    });

    if (!pageRows.length) {
      rect(margin, y - 24, usableWidth, 24);
      text(margin + 6, y - 15, 8, "NESSUN DATO DISPONIBILE CON I FILTRI SELEZIONATI", "F2");
      y -= 24;
    }

    line(margin, 52, width - margin, 52);
    if (pageIndex === pages.length - 1) {
      text(width - margin - 170, 36, 9, `Totale Valori Estratti: ${pages.flat().length}`, "F2");
    }
    commands.push("Q");

    const stream = commands.join("\n");
    const contentId = addObject(`<< /Length ${stream.length} >>\nstream\n${stream}\nendstream`);
    const pageId = objects.length + 1;
    pageObjectIds.push(pageId);
    addObject(`<< /Type /Page /Parent PAGES_PLACEHOLDER 0 R /MediaBox [0 0 ${width.toFixed(2)} ${height.toFixed(2)}] /Resources << /Font << /F1 ${fontRegular} 0 R /F2 ${fontBold} 0 R >> >> /Contents ${contentId} 0 R >>`);
  });

  const pagesId = objects.length + 1;
  for (let index = 0; index < objects.length; index += 1) {
    objects[index] = objects[index].replace(/PAGES_PLACEHOLDER/g, String(pagesId));
  }
  addObject(`<< /Type /Pages /Kids [${pageObjectIds.map((id) => `${id} 0 R`).join(" ")}] /Count ${pageObjectIds.length} >>`);
  const catalogId = addObject(`<< /Type /Catalog /Pages ${pagesId} 0 R >>`);

  let pdf = "%PDF-1.4\n";
  const offsets = [0];
  objects.forEach((object, index) => {
    offsets.push(pdf.length);
    pdf += `${index + 1} 0 obj\n${object}\nendobj\n`;
  });
  const xrefOffset = pdf.length;
  pdf += `xref\n0 ${objects.length + 1}\n0000000000 65535 f \n`;
  offsets.slice(1).forEach((offset) => {
    pdf += `${String(offset).padStart(10, "0")} 00000 n \n`;
  });
  pdf += `trailer\n<< /Size ${objects.length + 1} /Root ${catalogId} 0 R >>\nstartxref\n${xrefOffset}\n%%EOF`;

  const bytes = new Uint8Array(pdf.length);
  for (let index = 0; index < pdf.length; index += 1) bytes[index] = pdf.charCodeAt(index) & 0xff;
  return new Blob([bytes], { type: "application/pdf" });
}

function chunkRows(rows, size) {
  const chunks = [];
  for (let index = 0; index < rows.length; index += size) {
    chunks.push(rows.slice(index, index + size));
  }
  return chunks.length ? chunks : [[]];
}

function buildFilterSummary(filters, activeItem) {
  const summaryText = String(filters?.summaryText || "").trim();
  if (summaryText) return [summaryText];
  const summary = [];
  const query = String(filters.query || "").trim();
  summary.push(`Cerca dati: ${query || "Tutti"}`);
  summary.push(`Sede: ${filters.site || "Tutte le sedi"}`);
  summary.push(`Dal: ${formatDateForSummary(filters.from, "inizio periodo")}`);
  summary.push(`AL: ${formatDateForSummary(filters.to, "oggi")}`);
  getColumnFilterFields(activeItem).forEach((field) => {
    const value = String(filters.fieldFilters?.[field] || "").trim();
    summary.push(`${field}: ${value || "Tutti"}`);
  });
  return summary;
}

function makePreviewRows(activeItem, filters, type, reportRows = []) {
  const sourceRows = Array.isArray(reportRows) ? reportRows : [];
  return sourceRows.filter((row) => {
    const search = `${activeItem.title} ${row.join(" ")}`.toLowerCase();
    const query = String(filters.query || "").trim().toLowerCase();
    const matchesQuery = !query || search.includes(query);
    const matchesColumns = getColumnFilterFields(activeItem).every((field) => {
      const filterText = String(filters.fieldFilters?.[field] || "").trim().toLowerCase();
      if (!filterText) return true;
      const fieldIndex = activeItem.fields.indexOf(field);
      return String(row[fieldIndex] || "").toLowerCase().includes(filterText);
    });
    const selectedSite = String(filters.site || "").trim().toLowerCase();
    const siteFieldIndex = activeItem.fields.indexOf("Sede");
    const matchesSite = !selectedSite || selectedSite === "tutte le sedi" || siteFieldIndex < 0 || String(row[siteFieldIndex] || "").toLowerCase().includes(selectedSite);
    return matchesQuery && matchesSite && matchesColumns && rowMatchesDateRange(activeItem, row, filters);
  });
}

export function buildOfficeRows(activeItem, pages, filters) {
  const headerRows = [
    ["Report", activeItem.title],
    ["Filtri", buildFilterSummary(filters, activeItem).join(" | ")],
    ["Pagine", pages.length],
    [],
    ["Pagina", ...activeItem.fields],
  ];
  const bodyRows = pages.flatMap((pageRows, pageIndex) => pageRows.map((row) => [pageIndex + 1, ...row]));
  return [...headerRows, ...bodyRows];
}

export function buildCsv(activeItem, pages, filters) {
  return buildOfficeRows(activeItem, pages, filters).map((row) => row.map(csvEscape).join(";")).join("\n");
}

export function buildHtmlDocument(activeItem, pages, filters, companyName, variant, orientation = "portrait") {
  const tableHead = activeItem.fields.map((field) => `<th>${htmlEscape(field)}</th>`).join("");
  const title = htmlEscape(String(activeItem.title || "").toUpperCase());
  const company = htmlEscape(companyName || COMPANY_FALLBACK);
  const filterText = htmlEscape(buildFilterSummary(filters, activeItem).join(" · "));
  const lastPageIndex = Math.max(pages.length - 1, 0);
  const landscape = orientation === "landscape";
  const sheetWidth = landscape ? "297mm" : "210mm";
  const sheetHeight = landscape ? "210mm" : "297mm";
  const pageSize = landscape ? "A4 landscape" : "A4";
  const pageBlocks = pages.map((pageRows, pageIndex) => {
    const tableRows = pageRows.map((row) => `<tr>${row.map((cell) => `<td>${htmlEscape(cell)}</td>`).join("")}</tr>`).join("") || `<tr><td colspan="${activeItem.fields.length}">NESSUN DATO DISPONIBILE CON I FILTRI SELEZIONATI</td></tr>`;
    const footer = pageIndex === lastPageIndex ? `<footer>Totale Valori Estratti: ${pages.flat().length}</footer>` : `<footer class="empty"></footer>`;
    return `<section class="sheet"><header><div><strong class="company-title">${company.toUpperCase()}</strong><strong>${title}</strong></div><span>Pagina ${pageIndex + 1} di ${pages.length}</span></header><p>${filterText}</p><table><thead><tr>${tableHead}</tr></thead><tbody>${tableRows}</tbody></table>${footer}</section>`;
  }).join("");
  const hiddenNote = variant === "excel" ? "Documento esportato da VestaMNG per Excel." : "Documento esportato da VestaMNG per Word.";
  return `<!doctype html><html><head><meta charset="utf-8"><title>${title}</title><style>body{margin:0;background:#eef2f7;font-family:Arial,sans-serif;color:#0f172a}.sheet{display:flex;flex-direction:column;width:${sheetWidth};min-height:${sheetHeight};box-sizing:border-box;margin:12mm auto;padding:14mm;background:#fff;page-break-after:always;border:1px solid #d7dde8}.sheet:last-child{page-break-after:auto}header{display:flex;justify-content:space-between;align-items:center;gap:12px;border-bottom:1px solid #d7dde8;padding-bottom:8px;margin-bottom:8px}header div{min-width:0}header strong{display:block;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;font-size:15px}header em{display:block;margin-top:2px;overflow:hidden;color:#64748b;font-size:9px;font-style:normal;font-weight:800;text-overflow:ellipsis;text-transform:uppercase;white-space:nowrap}.company-title{margin-bottom:2px;text-transform:uppercase}header span,p{font-size:10px;font-weight:800;color:#64748b;text-transform:uppercase}p{text-align:center;margin:0 0 10px}table{width:100%;border-collapse:collapse;table-layout:fixed}th,td{border:1px solid #d7dde8;padding:5px 6px;font-size:10px;line-height:1.25;vertical-align:top;word-break:break-word}th{background:#eef7fb;font-weight:800;text-transform:uppercase}footer{margin-top:auto;padding-top:10px;border-top:1px solid #d7dde8;color:#64748b;font-size:11px;font-weight:900;letter-spacing:.08em;text-align:right;text-transform:uppercase}footer.empty{border-top:0;color:transparent}.hidden-note{display:none}@page{size:${pageSize};margin:0}@media print{body{background:#fff}.sheet{margin:0;border:0}}</style></head><body>${pageBlocks}<p class="hidden-note">${hiddenNote}</p></body></html>`;
}

export function downloadPdf(activeItem, pages, filters, companyName, orientation) {
  const filenameBase = slugify(activeItem.title);
  downloadBlob(createPdfBlob(activeItem, pages, filters, companyName, orientation), `${filenameBase}.pdf`);
}

export function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.setTimeout(() => URL.revokeObjectURL(url), 10000);
}

function getInitialFilters() {
  return {
    query: "",
    site: "Tutte le sedi",
    from: "",
    to: "",
    fieldFilters: {},
  };
}

function UtilityWindow({ title, description, items, ariaLabel, type }) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [filters, setFilters] = useState(() => getInitialFilters());
  const [currentPage, setCurrentPage] = useState(0);
  const [exportOpen, setExportOpen] = useState(false);
  const [exportError, setExportError] = useState("");
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [orientation, setOrientation] = useState("portrait");
  const [fullScreenOpen, setFullScreenOpen] = useState(false);
  const [reportRows, setReportRows] = useState([]);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportError, setReportError] = useState("");
  const [siteOptions, setSiteOptions] = useState(["Tutte le sedi"]);
  const location = useLocation();
  const activeItem = useMemo(() => items[activeIndex] || items[0], [items, activeIndex]);
  const companyName = useMemo(() => getCompanyDisplayName(), []);

  useEffect(() => {
    let active = true;
    getSites()
      .then((data) => {
        if (!active) return;
        const options = (Array.isArray(data?.items) ? data.items : [])
          .map((site) => site.description || "")
          .filter(Boolean);
        setSiteOptions(["Tutte le sedi", ...options]);
      })
      .catch(() => {
        if (active) setSiteOptions(["Tutte le sedi"]);
      });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    const reportSlug = new URLSearchParams(location.search || "").get("report");
    if (!reportSlug) return;
    const nextIndex = items.findIndex((item) => slugify(item.title) === reportSlug);
    if (nextIndex >= 0) {
      if (nextIndex !== activeIndex) setActiveIndex(nextIndex);
      setCurrentPage(0);
    }
  }, [activeIndex, items, location.search]);
  useEffect(() => {
    let active = true;
    setReportLoading(true);
    setReportRows([]);
    setReportError("");
    getUtilityReport(type, slugify(activeItem.title), {
      from: filters.from || undefined,
      to: filters.to || undefined,
      query: filters.query || undefined,
      site: filters.site && filters.site !== "Tutte le sedi" ? filters.site : undefined,
    })
      .then((data) => {
        if (!active) return;
        setReportRows(Array.isArray(data?.rows) ? data.rows : []);
      })
      .catch(() => {
        if (!active) return;
        setReportRows([]);
        setReportError("Impossibile caricare i dati dell'estrazione");
      })
      .finally(() => {
        if (active) setReportLoading(false);
      });

    return () => { active = false; };
  }, [activeItem.title, filters.from, filters.query, filters.site, filters.to, type]);

  const columnFilterFields = useMemo(() => getColumnFilterFields(activeItem), [activeItem]);
  const reportFilters = useMemo(() => {
    const dedicatedFilters = [
      { key: "query", label: "Cerca dati", type: "search", value: filters.query, placeholder: "Cerca nell\'anteprima", onChange: (value) => updateFilter("query", value) },
      { key: "site", label: "Sede:", type: "select", value: filters.site, options: siteOptions, onChange: (value) => updateFilter("site", value) },
      { key: "from", label: "Dal:", type: "date", value: filters.from, placeholder: "Dal", onChange: (value) => updateFilter("from", value) },
      { key: "to", label: "AL:", type: "date", value: filters.to, placeholder: "AL", onChange: (value) => updateFilter("to", value) },
      ...columnFilterFields.map((field) => ({
        key: `field-${field}`,
        label: `${field}:`,
        type: "text",
        value: filters.fieldFilters?.[field] || "",
        placeholder: `Filtra ${field.toLowerCase()}`,
        onChange: (value) => updateFieldFilter(field, value),
      })),
    ];
    return dedicatedFilters;
  }, [columnFilterFields, filters, siteOptions]);
  const visibleReportFilters = filtersExpanded ? reportFilters : reportFilters.slice(0, 4);
  const canExpandFilters = reportFilters.length > 4;
  const previewRows = useMemo(() => makePreviewRows(activeItem, filters, type, reportRows), [activeItem, filters, reportRows, type]);
  const rowsPerPage = orientation === "landscape" ? Math.max(8, ROWS_PER_PAGE - 3) : ROWS_PER_PAGE;
  const pages = useMemo(() => chunkRows(previewRows, rowsPerPage), [previewRows, rowsPerPage]);
  const safePage = Math.min(currentPage, Math.max(pages.length - 1, 0));
  const pageRows = pages[safePage] || [];

  function updateFilter(field, value) {
    setFilters((current) => ({ ...current, [field]: value }));
    setCurrentPage(0);
  }

  function updateFieldFilter(field, value) {
    setFilters((current) => ({ ...current, fieldFilters: { ...(current.fieldFilters || {}), [field]: value } }));
    setCurrentPage(0);
  }

  function selectItem(index) {
    setActiveIndex(index);
    setCurrentPage(0);
    setFilters(getInitialFilters());
    setExportOpen(false);
    setFiltersExpanded(false);
    setFullScreenOpen(false);
    setReportRows([]);
    setReportError("");
  }

  function goToPage(nextPage) {
    setCurrentPage(Math.min(Math.max(nextPage, 0), Math.max(pages.length - 1, 0)));
  }

  function printActive() {
    printUtilityPreview(orientation);
  }

  function toggleOrientation() {
    setOrientation((current) => current === "landscape" ? "portrait" : "landscape");
    setCurrentPage(0);
  }

  function renderPageSheet(pageIndex, rows, printMode = false) {
    return (
      <div className={`utility_page_sheet${!printMode ? " utility_screen_sheet" : ""}${orientation === "landscape" ? " is_landscape" : ""}`} key={`${activeItem.title}-${printMode ? "print" : "screen"}-${pageIndex}`}>
        <div className="utility_page_sheet_header">
          <div className="utility_page_sheet_title">
            <strong className="utility_company_name">{String(companyName).toUpperCase()}</strong>
            <strong>{String(activeItem.title).toUpperCase()}</strong>
          </div>
          <span>Pagina {pageIndex + 1} di {pages.length}</span>
        </div>
        <div className="utility_page_sheet_summary">{buildFilterSummary(filters, activeItem).join(" · ")}</div>
        <div className="utility_preview_table_wrap">
          <table className="utility_preview_table">
            <thead>
              <tr>{activeItem.fields.map((field) => <th key={field}>{field}</th>)}</tr>
            </thead>
            <tbody>
              {rows.map((row, rowIndex) => {
                return (
                  <tr key={`${activeItem.title}-${pageIndex}-${rowIndex}`}>
                    {row.map((cell, cellIndex) => <td key={`${activeItem.title}-${pageIndex}-${rowIndex}-${cellIndex}`}>{cell}</td>)}
                  </tr>
                );
              })}
              {!rows.length && (
                <tr>
                  <td colSpan={activeItem.fields.length}>NESSUN DATO DISPONIBILE CON I FILTRI SELEZIONATI</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="utility_page_sheet_footer">{pageIndex === pages.length - 1 ? `Totale Valori Estratti: ${previewRows.length}` : ""}</div>
      </div>
    );
  }

  function renderUtilityControls(extraClass = "") {
    return (
      <div className={`utility_export_stack${extraClass ? ` ${extraClass}` : ""}`}>
        <UtilityActionIconButton action="orientation" orientation={orientation} onClick={toggleOrientation} />
        <UtilityActionIconButton action="preview" onClick={() => setFullScreenOpen(true)} title="Espandi tutto" />
        <UtilityActionIconButton action="print" onClick={printActive} />
        <UtilityActionIconButton action="export" onClick={() => { setExportError(""); setExportOpen(true); }} />
      </div>
    );
  }


  function exportActive(format) {
    const filenameBase = slugify(activeItem.title);
    setExportError("");
    try {
      if (format === "csv") {
        downloadBlob(new Blob([`\ufeff${buildCsv(activeItem, pages, filters)}`], { type: "text/csv;charset=utf-8;" }), `${filenameBase}.csv`);
      } else if (format === "excel") {
        downloadBlob(createXlsxBlob({ title: activeItem.title, rows: buildOfficeRows(activeItem, pages, filters) }), `${filenameBase}.xlsx`);
      } else if (format === "word") {
        downloadBlob(createDocxTableBlob({
          title: activeItem.title,
          subtitle: `${companyName} · ${buildFilterSummary(filters, activeItem).join(" · ")}`,
          rows: [["Pagina", ...activeItem.fields], ...pages.flatMap((pageRows, pageIndex) => pageRows.map((row) => [pageIndex + 1, ...row]))],
          orientation,
        }), `${filenameBase}.docx`);
      } else if (format === "pdf") {
        downloadPdf(activeItem, pages, filters, companyName, orientation);
      }
      setExportOpen(false);
    } catch (_error) {
      setExportError("Impossibile esportare il file selezionato");
    }
  }

  return (
    <section className={`window_page bookings_page management_page management_shared_page utility_page ${orientation === "landscape" ? "is_landscape" : "is_portrait"}`}>
      <div className="window_banner">
        <div className="window_banner_copy">
          <h1>{title}</h1>
          <p>{description}</p>
        </div>
      </div>
      <div className="management_split management_tables_split utility_split">
        <aside className="management_side_panel management_tables_side utility_report_list" aria-label={ariaLabel}>
          <div className="utility_report_list_scroll">
            {items.map((item, index) => {
              const reportFocusId = `report:${slugify(item.title)}`;
              return (
                <button key={item.title} type="button" className={`management_catalog_button${index === activeIndex ? " is_active" : ""}`} data-vesta-focus-id={reportFocusId} onClick={() => selectItem(index)}>
                  <span className="management_catalog_label">{item.title}</span>
                </button>
              );
            })}
          </div>
        </aside>
        <div className="management_list_panel utility_detail_panel">
          <div className="utility_report_shell">
            <div className={`utility_filters_bar${filtersExpanded ? " is_expanded" : ""}`} aria-label="Filtri dedicati estrazione">
              {visibleReportFilters.map((filter) => (
                <label className={`utility_filter_field${filter.key === "query" ? " utility_filter_field_search" : ""}`} key={`${activeItem.title}-${filter.key}`}>
                  <span>{filter.label}</span>
                  {filter.type === "select" ? (
                    <VestaSelect value={filter.value} onChange={(event) => filter.onChange(event.target.value)}>
                      {filter.options.map((option) => <option key={option} value={option}>{option}</option>)}
                    </VestaSelect>
                  ) : filter.type === "date" ? (
                    <VestaDateInput value={filter.value} outputFormat="iso" placeholder={filter.placeholder || "gg/mm/aaaa"} onChange={filter.onChange} />
                  ) : (
                    <input type={filter.type} value={filter.value} onChange={(event) => filter.onChange(event.target.value)} placeholder={filter.placeholder} />
                  )}
                </label>
              ))}
              <button
                type="button"
                className="utility_filters_toggle vesta_filters_toggle"
                onClick={() => setFiltersExpanded((current) => !current)}
                disabled={!canExpandFilters}
                aria-expanded={filtersExpanded}
                aria-label={filtersExpanded ? "Nascondi filtri aggiuntivi" : "Mostra filtri aggiuntivi"}
                title={canExpandFilters ? (filtersExpanded ? "Nascondi filtri" : "Mostra tutti i filtri") : "Nessun altro filtro disponibile"}
              >
                <span className="utility_filters_toggle_icon vesta_filters_toggle_icon" aria-hidden="true">
                  <span />
                  <span />
                </span>
              </button>
            </div>

            <div className={`utility_preview_frame${reportLoading ? " is_loading" : ""}`}>
              <div className="utility_preview_topbar">
                <div className="utility_preview_identity">
                  <strong>{String(activeItem.title).toUpperCase()}</strong>
                  <div className="utility_description_marquee" role="text" aria-label={activeItem.description} title={activeItem.description}>
                    <div className="utility_description_track" aria-hidden="true">
                      <span>{activeItem.description}</span>
                      <span>{activeItem.description}</span>
                    </div>
                  </div>
                </div>
                <div className="utility_page_controls" aria-label="Navigazione pagine anteprima">
                  <button type="button" onClick={() => goToPage(0)} disabled={safePage === 0} aria-label="Prima pagina">«</button>
                  <button type="button" onClick={() => goToPage(safePage - 1)} disabled={safePage === 0} aria-label="Pagina precedente">‹</button>
                  <strong>{safePage + 1} / {pages.length}</strong>
                  <button type="button" onClick={() => goToPage(safePage + 1)} disabled={safePage >= pages.length - 1} aria-label="Pagina successiva">›</button>
                  <button type="button" onClick={() => goToPage(pages.length - 1)} disabled={safePage >= pages.length - 1} aria-label="Ultima pagina">»</button>
                </div>
                {renderUtilityControls()}
              </div>

              <div className="utility_filter_summary">{reportLoading ? "Caricamento dati reali..." : (reportError || buildFilterSummary(filters, activeItem).join(" · "))}</div>{reportLoading && <div className="utility_loading_overlay" role="status" aria-live="polite"><span className="utility_loading_spinner" aria-hidden="true"><span /></span><strong>Caricamento dati</strong></div>}

              <div className="utility_preview_print_area">
                {renderPageSheet(safePage, pageRows)}
                <div className="utility_print_pages" aria-hidden="true">
                  {pages.map((printRows, printPageIndex) => renderPageSheet(printPageIndex, printRows, true))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <UtilityFullscreenPreview
        open={fullScreenOpen}
        orientation={orientation}
        currentPage={safePage}
        pageCount={pages.length}
        loading={reportLoading}
        onPageChange={goToPage}
        onPrint={printActive}
        onExport={() => { setExportError(""); setExportOpen(true); }}
        onToggleOrientation={toggleOrientation}
        onClose={() => setFullScreenOpen(false)}
      >
        {renderPageSheet(safePage, pageRows)}
      </UtilityFullscreenPreview>

      <UtilityExportOverlay
        open={exportOpen}
        title={activeItem.title}
        documentMode
        formats={["pdf", "excel", "csv", "word"]}
        error={exportError}
        onClose={() => setExportOpen(false)}
        onExport={exportActive}
      />
    </section>
  );
}

export function Prints() {
  return <UtilityWindow title="STAMPE" description="Elenco stampe operative per reception, camere, contabilità e controllo giornaliero." items={PRINT_ITEMS} ariaLabel="Elenco stampe" type="prints" />;
}

export function Statistics() {
  return <UtilityWindow title="STATISTICHE" description="Tipologie di estrazioni statistiche per leggere andamento, occupazione, ospiti e operatività." items={STAT_ITEMS} ariaLabel="Elenco estrazioni statistiche" type="statistics" />;
}

export function Profitability() {
  return <UtilityWindow title="REDDITIVITÀ" description="Tipologie di estrazioni economiche per analizzare ricavi, margini, canali e performance." items={PROFIT_ITEMS} ariaLabel="Elenco estrazioni redditività" type="profitability" />;
}
